/*
* Copyright (c) 2009-2022. Authors: see NOTICE file.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*      http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

// The IMS ChatHost adapter (FR-09, integration-contract.md §3): the ONLY place web-ui constructs
// the `ChatHost` object the island reads through `useChatHost()`. A factory over a Vue instance
// (not a composable/hook — Vue 2.7's Options API components don't have one), called from
// `ChatIslandMount.vue`'s `mounted()` (and again on a locale change — see its own watcher).
import { updateToken } from '@/utils/token-utils';
import constants from '@/utils/constants.js';
import { setChatBadgeUnreadCount } from './chatBadgeState';
import { SHARE_IN_CHAT_EVENT } from '@/chat/shareBus';

const IMS_CHAT_ACCENT = '#2f6df6';
const IMS_CHAT_ACCENT_SOFT = '#eef3ff';
const IMS_CHAT_DANGER = '#e2466b';

// The docked window + fullscreen overlay (ChatWindow.tsx / ChatIslandShell.tsx) both size
// themselves off `host.fullscreenTopOffset`, so it needs to be the ACTUAL rendered height of IMS's
// own top bar, not a guess. A hand-measured "Bulma .navbar is 50px + a 1px border = 51" estimate
// turned out wrong against the real deployed header (left a whitespace gap between the header and
// the docked window) — so this measures `ImsNavbar.vue`'s root `<nav class="navbar">` element live
// via `getBoundingClientRect()` instead. `nav.navbar` is that component's single top-level element
// (see its <template> root) — it's the one persistent top bar mounted once in `App.vue`, so this
// selector is unambiguous. Falls back to the old 51 estimate only if the element isn't in the DOM
// yet (shouldn't happen in practice — see `ChatIslandMount.vue`'s `$nextTick` before calling this).
const NAV_SELECTOR = 'nav.navbar';
const FALLBACK_FULLSCREEN_TOP_OFFSET = 51;

export function measureFullscreenTopOffset() {
  if (typeof document === 'undefined') {
    return FALLBACK_FULLSCREEN_TOP_OFFSET;
  }
  const nav = document.querySelector(NAV_SELECTOR);
  if (!nav) {
    return FALLBACK_FULLSCREEN_TOP_OFFSET;
  }
  const height = nav.getBoundingClientRect().height;
  return height > 0 ? Math.round(height) : FALLBACK_FULLSCREEN_TOP_OFFSET;
}

// AC-8.1: gates the composer's "Share current slide" entry — mirrors the pre-migration
// ChatWindow.vue `canShareSlide` getter exactly (same store path, same wrapper/imageInstance
// check), just read directly off the Vue instance's `$store` instead of a component computed.
function canShareSlide(vueInstance) {
  const viewer = vueInstance.$store.getters['currentProject/currentViewer'];
  if (!viewer || !viewer.images) {
    return false;
  }
  const wrapper = viewer.images[viewer.activeImage] || viewer.images[Object.keys(viewer.images)[0]];
  return !!(wrapper && wrapper.imageInstance);
}

// M2 fix F-M2.3 (AC-8.1): builds the `SharePayload` for the composer's "Share current slide" —
// ported verbatim from the retired `ChatWindow.vue`'s `onShareCurrentSlide()` (same viewer-state
// read, same fields). Called by `ChatIslandMount.vue`'s `ZC_SHARE_CURRENT_SLIDE_EVENT` listener,
// which then re-emits the result on the SAME `shareBus` `onShareRequest` already subscribes to —
// one mechanism for both AC-8.1 and AC-8.2, per the reviewer's fix.
export function buildCurrentSlideSharePayload(vueInstance) {
  const viewer = vueInstance.$store.getters['currentProject/currentViewer'];
  if (!viewer || !viewer.images) {
    return null;
  }
  const wrapper = viewer.images[viewer.activeImage] || viewer.images[Object.keys(viewer.images)[0]];
  const image = wrapper && wrapper.imageInstance;
  if (!image) {
    return null;
  }
  return {
    kind: 'wsi',
    ref: `image:${image.id}`,
    deep_link: `/project/${image.project}/image/${image.id}`,
    thumb: image.thumbURL(200),
  };
}

// Builds the ChatHost for the CURRENT locale/viewer-state snapshot. `ChatIslandMount.vue` re-calls
// `mount(el, buildImsChatHost(this))` on a locale change (mount() is idempotent — re-renders into
// the existing root) so `host.locale`/`host.t` stay live without the island owning locale state
// (AC-9.5); `canShareSlide` is read fresh on every call for the same reason (AC-8.1).
export function buildImsChatHost(vueInstance) {
  return {
    platform: 'ims',
    chatBaseUrl: constants.CHAT_URL || '/chat/v1',
    getAccessToken: () => updateToken(30),
    t: (key, vars) => vueInstance.$t(`chat.${key}`, vars),
    locale: vueInstance.$i18n.locale,
    theme: { accent: IMS_CHAT_ACCENT, accentSoft: IMS_CHAT_ACCENT_SOFT, danger: IMS_CHAT_DANGER },
    // AC-3.5/AC-8.3: "open in viewer" (slide/annotation deep links) via the Vue router.
    navigate: (deepLink) => vueInstance.$router.push(deepLink),
    // AC-11.1/FR-16: host-chrome unread badge — the IMS top-bar Chat entry (`ChatTopBarEntry.vue`)
    // reads this reactive singleton instead of a Vuex chat module (retired).
    onUnreadChange: (count) => setChatBadgeUnreadCount(count),
    // AC-3.6/AC-8.1/AC-8.2: the viewer's "Share in chat" seam (any non-chat component — the
    // annotation panel — can open the island's share picker without importing chat internals; see
    // src/chat/shareBus.js, unchanged from pre-migration). Subscribing/unsubscribing on the app's
    // existing global `$eventBus` (installed on `Vue.prototype` in main.js).
    onShareRequest: (handler) => {
      vueInstance.$eventBus.$on(SHARE_IN_CHAT_EVENT, handler);
      return () => vueInstance.$eventBus.$off(SHARE_IN_CHAT_EVENT, handler);
    },
    canShareSlide: canShareSlide(vueInstance),
    shareLabels: { default: 'Shared slide' },
    // Docked window + fullscreen overlay must sit below IMS's own top navbar instead of under/
    // behind it — measured live (see `measureFullscreenTopOffset` above) rather than guessed, since
    // a hand-measured constant didn't match the real deployed header. Read fresh on every call (like
    // `canShareSlide`), so a locale-change re-mount also re-measures — a reasonable, if imperfect,
    // way to reflect layout changes (e.g. IMS's viewer-mode `topMenuHidden` toggle) without a
    // dedicated resize/mutation observer.
    fullscreenTopOffset: measureFullscreenTopOffset(),
  };
}
